import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

export interface OrderChequePayment {
  id: string;
  chequeNumber: string;
  chequeDate: string;
  chequeRealizedDate: string | null;
  paymentDate: string;
  chequeStatus: number;
  customerName: string;
  orderId: string;
  paymentStatus: string;
}

export interface OrderChequePaymentResponse {
  data: OrderChequePayment[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface OrderChequePaymentFilters {
  customerId?: string;
  chequeStatus?: number | string;
  chequeStartDate?: string | null;
  chequeEndDate?: string | null;
  paymentStartDate?: string | null;
  paymentEndDate?: string | null;
  chequeNumber?: string;
}

export const getOrderChequePayments = async (
  offset = 1,
  limit = 15,
  sortBy = 'chequeDate',
  order: 'ASC' | 'DESC' = 'DESC',
  filters?: OrderChequePaymentFilters
): Promise<OrderChequePaymentResponse> => {
  const params: Record<string, string | number> = {
    offset,
    limit,
    sortBy,
    order,
  };

  if (filters?.customerId) {
    params.customerId = filters.customerId;
  }
  if (filters?.chequeStatus && filters.chequeStatus !== 'all') {
    params.chequeStatus = filters.chequeStatus;
  }
  if (filters?.chequeStartDate) {
    params.chequeStartDate = filters.chequeStartDate;
  }
  if (filters?.chequeEndDate) {
    params.chequeEndDate = filters.chequeEndDate;
  }
  if (filters?.paymentStartDate) {
    params.paymentStartDate = filters.paymentStartDate;
  }
  if (filters?.paymentEndDate) {
    params.paymentEndDate = filters.paymentEndDate;
  }
  if (filters?.chequeNumber) {
    params.chequeNumber = filters.chequeNumber;
  }

  const response = await axiosTokenInstance.get<OrderChequePaymentResponse>(
    '/orders/get-order-cheque-payments',
    {
      params,
      withCredentials: true,
    }
  );

  return response.data;
};

export interface UpdateOrderChequeStatusPayload {
  id: string;
  chequeStatus: number;
  chequeRealizedDate?: string | null;
}

export const updateOrderChequeStatus = async (
  payload: UpdateOrderChequeStatusPayload
): Promise<{ message: string }> => {
  const response = await axiosTokenInstance.patch<{ message: string }>(
    '/orders/update-order-payment-cheque-status',
    payload,
    {
      withCredentials: true,
    }
  );

  return response.data;
};

// GRN Cheque Payment Types and Functions
export interface GRNChequePayment {
  id: string;
  chequeNumber: string;
  chequeDate: string;
  chequeRealizedDate: string | null;
  paymentDate: string;
  chequeStatus: number | null;
  vendorName: string;
  grnId: string;
  paymentStatus: number;
}

export interface GRNChequePaymentResponse {
  data: GRNChequePayment[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface GRNChequePaymentFilters {
  vendorId?: string;
  chequeStatus?: number | string;
  chequeStartDate?: string | null;
  chequeEndDate?: string | null;
  paymentStartDate?: string | null;
  paymentEndDate?: string | null;
  search?: string;
}

export const getGRNChequePayments = async (
  offset = 1,
  limit = 15,
  sortBy = 'chequeDate',
  order: 'ASC' | 'DESC' = 'DESC',
  filters?: GRNChequePaymentFilters
): Promise<GRNChequePaymentResponse> => {
  const params: Record<string, string | number> = {
    offset,
    limit,
    sortBy,
    order,
  };

  if (filters?.vendorId) {
    params.vendorId = filters.vendorId;
  }
  if (filters?.chequeStatus && filters.chequeStatus !== 'all') {
    params.chequeStatus = filters.chequeStatus;
  }
  if (filters?.chequeStartDate) {
    params.chequeStartDate = filters.chequeStartDate;
  }
  if (filters?.chequeEndDate) {
    params.chequeEndDate = filters.chequeEndDate;
  }
  if (filters?.paymentStartDate) {
    params.paymentStartDate = filters.paymentStartDate;
  }
  if (filters?.paymentEndDate) {
    params.paymentEndDate = filters.paymentEndDate;
  }
  if (filters?.search) {
    params.search = filters.search;
  }

  const response = await axiosTokenInstance.get<GRNChequePaymentResponse>(
    '/received_goods/get-grn-cheque-payments',
    {
      params,
      withCredentials: true,
    }
  );

  return response.data;
};

export interface UpdateGRNChequeStatusPayload {
  id: string;
  chequeStatus: number;
  chequeRealizedDate?: string | null;
}

export const updateGRNChequeStatus = async (
  payload: UpdateGRNChequeStatusPayload
): Promise<{ message: string }> => {
  const response = await axiosTokenInstance.patch<{ message: string }>(
    '/received_goods/update-grn-payment-cheque-status',
    payload,
    {
      withCredentials: true,
    }
  );

  return response.data;
};

// Voucher Cheque Payment Types and Functions
export interface VoucherChequePayment {
  id: string;
  chequeNumber: string;
  chequeDate: string;
  chequeRealizedDate: string | null;
  paymentDate: string;
  chequeStatus: number | null;
  payee: string;
  voucherId: string;
  paymentStatus: string;
}

export interface VoucherChequePaymentResponse {
  data: VoucherChequePayment[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface VoucherChequePaymentFilters {
  chequeStatus?: number | string;
  chequeStartDate?: string | null;
  chequeEndDate?: string | null;
  paymentStartDate?: string | null;
  paymentEndDate?: string | null;
  search?: string;
}

export const getVoucherChequePayments = async (
  offset = 1,
  limit = 15,
  sortBy = 'chequeDate',
  order: 'ASC' | 'DESC' = 'DESC',
  filters?: VoucherChequePaymentFilters
): Promise<VoucherChequePaymentResponse> => {
  const params: Record<string, string | number> = {
    offset,
    limit,
    sortBy,
    order,
  };

  if (filters?.chequeStatus && filters.chequeStatus !== 'all') {
    params.chequeStatus = filters.chequeStatus;
  }
  if (filters?.chequeStartDate) {
    params.chequeStartDate = filters.chequeStartDate;
  }
  if (filters?.chequeEndDate) {
    params.chequeEndDate = filters.chequeEndDate;
  }
  if (filters?.paymentStartDate) {
    params.paymentStartDate = filters.paymentStartDate;
  }
  if (filters?.paymentEndDate) {
    params.paymentEndDate = filters.paymentEndDate;
  }
  if (filters?.search) {
    params.search = filters.search;
  }

  const response = await axiosTokenInstance.get<VoucherChequePaymentResponse>(
    '/voucher-payments/get-voucher-cheque-payments',
    {
      params,
      withCredentials: true,
    }
  );

  return response.data;
};

export interface UpdateVoucherChequeStatusPayload {
  id: string;
  chequeStatus: number;
  chequeRealizedDate?: string | null;
}

export const updateVoucherChequeStatus = async (
  payload: UpdateVoucherChequeStatusPayload
): Promise<{ message: string }> => {
  const response = await axiosTokenInstance.patch<{ message: string }>(
    '/voucher-payments/update-voucher-payment-cheque-status',
    payload,
    {
      withCredentials: true,
    }
  );

  return response.data;
};
